import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, User, FileText, ChevronRight, History, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EditHistoryDialog({ open, onOpenChange, resourceType, resourceId, pageName }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !resourceType || !resourceId) return;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/edit-history/${resourceType}/${resourceId}?limit=30`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(res.data?.history || []);
      } catch { setHistory([]); }
      finally { setLoading(false); }
    };
    fetchHistory();
  }, [open, resourceType, resourceId]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getRelativeTime = (ts) => {
    if (!ts) return '';
    const diffMs = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'baru sahaja';
    if (mins < 60) return `${mins} minit lalu`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} jam lalu`;
    const days = Math.floor(hrs / 24);
    return `${days} hari lalu`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[80vh]" data-testid="edit-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} className="text-[hsl(var(--primary))]" />
            Edit History
            {pageName && <Badge className="bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20 text-[10px] ml-2">{pageName}</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[60vh] pr-1 -mr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[hsl(var(--primary))]" size={24} />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={36} className="mx-auto text-[hsl(var(--text-muted))]/30 mb-3" />
              <p className="text-sm text-[hsl(var(--text-muted))]">Tiada rekod edit</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-3 bottom-3 w-px bg-[hsl(var(--border))]" />
              
              <div className="space-y-1">
                {history.map((entry, idx) => (
                  <div key={entry.id || idx} className="relative pl-12 py-3 hover:bg-[hsl(var(--surface))]/50 rounded-lg transition-colors" data-testid={`history-entry-${idx}`}>
                    {/* Timeline dot */}
                    <div className={`absolute left-3.5 top-5 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))]' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]'}`} />
                    
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-[hsl(var(--text-primary))]">
                        {entry.user_name || entry.user_email || 'Unknown'}
                      </span>
                      <span className="text-[10px] text-[hsl(var(--text-muted))]">{getRelativeTime(entry.timestamp)}</span>
                    </div>
                    
                    {/* Action */}
                    <div className="flex items-center gap-2 mb-1.5">
                      {entry.action && <Badge className={`text-[10px] border ${
                        entry.action === 'ADD' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                        entry.action === 'EDIT' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                        entry.action === 'DELETE' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                        entry.action === 'IMPORT' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                        'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}>{entry.action}</Badge>}
                      {entry.section && <Badge className="bg-violet-500/10 text-violet-400 border border-violet-500/20 text-[10px]">{entry.section}</Badge>}
                      {entry.page && <span className="text-xs text-[hsl(var(--text-muted))]">{entry.page}</span>}
                    </div>
                    
                    {/* Changes detail */}
                    {entry.changes && entry.changes.length > 0 && (
                      <div className="bg-[hsl(var(--surface-elevated))] rounded-md p-2 border border-[hsl(var(--border))] mt-1">
                        {entry.changes.map((change, ci) => (
                          <div key={ci} className="flex items-start gap-2 text-xs py-0.5">
                            <ChevronRight size={10} className="text-[hsl(var(--text-muted))] mt-0.5 flex-shrink-0" />
                            <span className="text-[hsl(var(--text-secondary))]">
                              <span className="font-medium text-[hsl(var(--text-primary))]">{change.field || 'Data'}</span>
                              {change.from !== undefined && <span className="text-red-400/70"> {String(change.from).slice(0, 40)}</span>}
                              {change.to !== undefined && <span className="text-emerald-400"> {String(change.to).slice(0, 40)}</span>}
                              {!change.from && !change.to && change.detail && <span> — {change.detail}</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Fallback for activity_logs format */}
                    {entry.details && !entry.changes && (
                      <p className="text-xs text-[hsl(var(--text-muted))]">{entry.details}</p>
                    )}
                    
                    {/* Timestamp */}
                    <p className="text-[10px] text-[hsl(var(--text-muted))]/60 mt-1">{formatTime(entry.timestamp)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EditHistoryDialog;
